import React from 'react';
import { motion as Motion, AnimatePresence } from 'framer-motion';
import { X, Trash2, ArrowRight, ShoppingBag } from 'lucide-react';
import { useCart } from '../hooks/useCart';

const CartOverlay = ({ activeColor, lang = 'en' }) => {
  const { cartItems, removeItem, isCartOpen, setIsCartOpen, totalAmount } = useCart();
  const themeColor = activeColor || cartItems[0]?.color || 'var(--text-color)';

  const TRANSLATIONS = {
    de: {
      title: "Booster",
      items: "Sprints",
      empty: "Dein Warenkorb ist leer.",
      continue: "Weiter stöbern",
      sprintBase: "Basis Sprint (10 Tage)",
      expansion: "Zusatzmodule",
      campaignTotal: "Gesamtbetrag Sprint",
      dueToday: "Heute fällig (50%)",
      totalAmount: "Gesamtbetrag",
      cta: "Sichere dir deinen Sprint",
      terms: "Mit dem Fortfahren erklärst du dich mit den Servicebedingungen von CRE8NOW einverstanden. Alle Sprints unterliegen der Verfügbarkeit."
    },
    en: {
      title: "Booster",
      items: "Items",
      empty: "Your cart is empty.",
      continue: "Continue Browsing",
      sprintBase: "Standard 10-Day Sprint",
      expansion: "Expansion Modules",
      campaignTotal: "Campaign Total",
      dueToday: "Due Today (50%)",
      totalAmount: "Total Amount",
      cta: "Secure Your Sprint",
      terms: "By proceeding, you agree to CRE8NOW's Terms of Service. All sprints are subject to availability."
    }
  };

  const t = TRANSLATIONS[lang] || TRANSLATIONS.en;

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          {/* Backdrop */}
          <Motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsCartOpen(false)}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
          />
          
          {/* Cart Panel */}
          <Motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 bottom-0 w-full md:w-[450px] bg-bg-color border-l border-border-color z-[9999] shadow-2xl flex flex-col h-full"
          >
            {/* Header */}
            <div className="px-4 md:px-5 h-16 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-surface-color rounded-full flex items-center justify-center shrink-0" style={{ color: themeColor }}>
                  <ShoppingBag size={16} />
                </div>
                <h4 className="text-lg font-black tracking-tight" style={{ lineHeight: '32px' }}>{t.title}</h4>
              </div>
              <button 
                onClick={() => setIsCartOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-surface-color transition-colors shrink-0"
              >
                <X size={18} />
              </button>
            </div>

            {/* Items List */}
            <div 
              className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6 space-y-4"
            >
              {cartItems.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                  <div className="w-16 h-16 bg-surface-color rounded-full flex items-center justify-center" style={{ color: themeColor }}>
                    <ShoppingBag size={32} />
                  </div>
                  <p className="text-text-muted font-light">{t.empty}</p>
                  <button 
                    onClick={() => setIsCartOpen(false)}
                    className="text-xs font-black uppercase tracking-widest underline underline-offset-8"
                  >
                    {t.continue}
                  </button>
                </div>
              ) : (
                cartItems.map((item) => (
                  <div 
                    key={item.id} 
                    className="group relative border border-border-color rounded-lg overflow-hidden transition-all duration-500 hover:border-opacity-50 bg-white/[0.03]"
                    style={{ borderColor: `${item.color}33` }}
                  >
                    {/* Campaign Card Header */}
                    <div className="p-4 border-b border-border-color bg-white/[0.05] flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: item.color }} />
                          <h6 className="text-[9px] font-black uppercase tracking-[0.2em]" style={{ color: item.color }}>Sprint Active</h6>
                        </div>
                        <h5 className="font-black tracking-tight leading-none mb-2">{item.title}</h5>
                        <p className="text-[8px] text-text-muted font-light uppercase tracking-widest leading-none">{item.tagline}</p>
                      </div>
                      <button 
                        onClick={() => removeItem(item.id)}
                        className="w-7 h-7 flex items-center justify-center rounded-full text-text-muted hover:bg-red-500 hover:text-white transition-all duration-300"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>

                    {/* Modules/Details */}
                    <div className="p-4 space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-text-muted font-light text-sm">{t.sprintBase}</span>
                        <span className="text-sm font-mono font-bold">€{item.totals.subtotalBase.toLocaleString()}</span>
                      </div>
                      
                      {item.selectedModules && item.selectedModules.length > 0 && (
                        <div className="space-y-1.5 pt-2 border-t border-border-color/30">
                          <p className="text-[8px] font-black uppercase tracking-[0.2em] text-text-muted">{t.expansion}</p>
                          {item.selectedModules.map(mId => (
                            <div key={mId} className="flex justify-between">
                              <span className="text-text-muted font-light text-sm">{item.moduleDetails?.[mId]?.name || mId}</span>
                              <span className="text-sm font-mono font-bold">+€{item.moduleDetails?.[mId]?.price?.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Total for this card */}
                      <div className="flex justify-between items-end pt-2 border-t border-border-color/20">
                        <span className="text-[9px] font-black uppercase tracking-widest text-text-muted">{t.campaignTotal}</span>
                        <span className="text-3xl font-mono font-black">€{item.totals.grandTotal.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer — Totals */}
            {cartItems.length > 0 && (
              <div className="px-4 md:px-6 pt-4 pb-3 bg-surface-color/50 border-t border-border-color space-y-1.5">
                <div className="flex justify-between items-center">
                  <span className="text-[9px] font-bold uppercase tracking-widest text-text-color opacity-80">{t.dueToday}</span>
                  <span className="text-3xl font-mono font-black" style={{ color: themeColor }}>€{(totalAmount * 0.5).toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-text-muted">{t.totalAmount}</span>
                  <span className="text-xl font-mono font-black text-text-muted">€{totalAmount.toLocaleString()}</span>
                </div>
              </div>
            )}

            {/* CTA — Fixed Bottom */}
            {cartItems.length > 0 && (
              <div className="bg-surface-color border-t border-border-color">
                <p className="text-text-muted/50 font-light leading-relaxed text-center px-6 py-2" style={{ fontSize: '11px' }}>
                  {t.terms}
                </p>
                <button 
                  className="group w-full py-5 text-white font-black uppercase text-xs tracking-[0.2em] rounded-none flex items-center justify-center transition-all duration-300"
                  style={{ backgroundColor: themeColor }}
                >
                  <span className="flex items-center">
                    {t.cta}
                    <span className="w-0 opacity-0 group-hover:w-5 group-hover:ml-3 group-hover:opacity-100 transition-all duration-500 overflow-hidden flex items-center">
                      <ArrowRight size={16} />
                    </span>
                  </span>
                </button>
              </div>
            )}
          </Motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartOverlay;
